import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Box,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { fetchShuntMetrics } from '../../../services/shuntMetricsService';
import { processShuntData, formatShuntCurrent } from '../../../utils/shuntDataProcessor';

const timeFilters = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' }
];

const metricOptions = [
  { value: 'shunt_current', label: 'Shunt Current' },
  { value: 'shunt_soc', label: 'Shunt SOC' }
];

const ShuntMetrics = ({ emsName }) => {
  const [timeFilter, setTimeFilter] = useState('daily');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('shunt_current');

  const fetchData = useCallback(async () => {
    if (!emsName) return;
    
    try {
      setLoading(true);
      setError(null);
      const formattedDate = selectedDate.toLocaleDateString('en-CA');
      const response = await fetchShuntMetrics(
        emsName,
        timeFilter,
        formattedDate
      );
      
      if (response.success) {
        const processedData = processShuntData(response.data);
        
        setData(processedData);
      } else {
        setError(response.error);
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError(err.message || 'Failed to load shunt metrics');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [emsName, timeFilter, selectedDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatXAxis = useCallback((timestamp) => {
    const date = new Date(timestamp);
    switch(timeFilter) {
      case 'monthly':
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      case 'weekly':
        return date.toLocaleDateString('en-US', { weekday: 'short' });
      case 'daily':
        return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      default:
        return date.toLocaleString();
    }
  }, [timeFilter]);

  const renderChart = useCallback(() => {
    if (!data.length) return null;
    
    // Debug log to check data structure
    console.log('Chart data sample:', data[0]);

    const chartCommonProps = {
      margin: { top: 5, right: 30, left: 20, bottom: 5 },
      height: 400,
      width: 900,
    };

    return (
      <Box sx={{ 
        width: chartCommonProps.width,
        height: chartCommonProps.height
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={data}
            {...chartCommonProps}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="timestamp"
              tickFormatter={formatXAxis}
              type="number"
              domain={['auto', 'auto']}
              scale="time"
            />
            <YAxis 
              domain={['auto', 'auto']}
              tickFormatter={value => {
                if (selectedMetric === 'shunt_current') {
                  return `${value.toFixed(2)} A`;
                } else{
                return `${value}%`;
                }
              }}
            />
            <Tooltip 
              labelFormatter={(label) => new Date(label).toLocaleString()}
              formatter={(value, name) => {
                if (selectedMetric === 'shunt_current') {
                  return [`${value.toFixed(2)} A`, 'Shunt Current'];
                } else{
                    return [`${value}%`, 'Shunt SOC'];
                }
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey={selectedMetric}
              name={selectedMetric === 'shunt_current' ? 'Shunt Current' : 'Shunt SOC'}
              stroke="#8884d8" 
              dot={false}
              isAnimationActive={false}
              strokeWidth={2}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  }, [data, selectedMetric, formatXAxis]);

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Box sx={{ width: 'fit-content' }}>
      <Box sx={{ 
        display: 'flex',
        gap: 2,
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        justifyContent: 'flex-start',
        '& .MuiFormControl-root': {
          minWidth: 180
        }
      }}>
        <FormControl size="small">
          <InputLabel>Metric</InputLabel>
          <Select
            value={selectedMetric}
            label="Metric"
            onChange={(e) => setSelectedMetric(e.target.value)}
            MenuProps={{
              sx: {
                '& .MuiMenu-paper': {
                  '& .MuiMenuItem-root': {
                    padding: '8px 16px',
                    margin: '4px 0',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }
                }
              }
            }}
          >
            {metricOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeFilter}
            label="Time Range"
            onChange={(e) => setTimeFilter(e.target.value)}
            MenuProps={{
              sx: {
                '& .MuiMenu-paper': {
                  '& .MuiMenuItem-root': {
                    padding: '8px 16px',
                    margin: '4px 0',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }
                }
              }
            }}
          >
            {timeFilters.map((filter) => (
              <MenuItem key={filter.value} value={filter.value}>
                {filter.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Select Date"
            value={selectedDate}
            onChange={(date) => date && setSelectedDate(date)}
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true
              }
            }}
          />
        </LocalizationProvider>
      </Box>

      {data.length > 0 ? (
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle1" gutterBottom>
            {selectedMetric === 'shunt_current' ? 'Shunt Current' : 'Shunt SOC'}
          </Typography>
          {renderChart()}
        </Box>
      ) : (
        <Alert severity="info">No data available for the selected period</Alert>
      )}
    </Box>
  );
};

export default React.memo(ShuntMetrics);